import { useState } from 'react';
import { Modal } from 'reactstrap';
import { IconClipboardCheck } from '@tabler/icons-react';
import { PreSaleVisitFeedbackBody } from '../services/dashboardService';
import {
  FeedbackOption,
  OBJECTION_OPTIONS,
  PROSPECT_STAGE_OPTIONS,
  PURCHASE_INTENT_OPTIONS,
} from '../constants/preSaleFeedbackOptions';

export type PreSaleVisitFeedback = PreSaleVisitFeedbackBody;

type Option = FeedbackOption;
const INTENTS = PURCHASE_INTENT_OPTIONS;
const OBJECTIONS = OBJECTION_OPTIONS;
const STAGES = PROSPECT_STAGE_OPTIONS;

interface Props {
  isOpen: boolean;
  projectName: string;
  models: Option[];
  error?: string;
  loading?: boolean;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (feedback: PreSaleVisitFeedback) => void;
}

export const Chips = ({
  options,
  value,
  onToggle,
}: {
  options: Option[];
  value: string[];
  onToggle: (value: string) => void;
}) => (
  <div className="vfm-chips">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={`vfm-chip${value.includes(option.value) ? ' is-active' : ''}`}
        aria-pressed={value.includes(option.value)}
        onClick={() => onToggle(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const PreSaleVisitFeedbackModal = ({ isOpen, projectName, models, error, loading, submitLabel = 'Finalizar visita', onClose, onSubmit }: Props) => {
  const [unit, setUnit] = useState('');
  const [intent, setIntent] = useState('');
  const [objections, setObjections] = useState<string[]>([]);
  const [comparedWith, setComparedWith] = useState('');
  const [stage, setStage] = useState('');
  const [tried, setTried] = useState(false);

  const valid = !!unit && !!intent && !!stage;

  const toggleObjection = (o: string) =>
    setObjections((prev) => (prev.includes(o) ? prev.filter((x) => x !== o) : [...prev, o]));

  const submit = () => {
    setTried(true);
    if (!valid) return;
    onSubmit({
      project_model_id: unit,
      purchase_intent: intent,
      objections,
      compared_with: comparedWith.trim() || undefined,
      prospect_stage: stage,
    });
  };

  const err = (missing: boolean) =>
    tried && missing ? <small className="vfm-error">Este campo es obligatorio</small> : null;

  return (
    <Modal isOpen={isOpen} toggle={loading ? undefined : onClose} centered size="lg" className="vfm">
      <div className="vfm-body">
        <div className="vfm-head">
          <span className="vfm-icon"><IconClipboardCheck size={24} /></span>
          <div>
            <h2 className="vfm-title">¿Cómo te fue en la visita?</h2>
            <p className="vfm-sub">Llena esto para cerrar la visita a {projectName}.</p>
          </div>
        </div>

        <div className="vfm-field">
          <label>¿Qué tipología o unidad le interesó más? <b>*</b></label>
          {models.length === 0 ? (
            <small className="text-muted">Este proyecto no tiene tipologías cargadas.</small>
          ) : (
            <Chips options={models} value={unit ? [unit] : []} onToggle={setUnit} />
          )}
          {err(!unit)}
        </div>

        <div className="vfm-field">
          <label>¿Qué tan real ves la intención de compra? <b>*</b></label>
          <Chips options={INTENTS} value={intent ? [intent] : []} onToggle={setIntent} />
          {err(!intent)}
        </div>

        <div className="vfm-field">
          <label>¿Qué objeciones escuchaste durante la visita? <span>(opcional)</span></label>
          <Chips options={OBJECTIONS} value={objections} onToggle={toggleObjection} />
        </div>

        <div className="vfm-field">
          <label>¿Con qué otro proyecto o desarrolladora lo comparó? <span>(opcional)</span></label>
          <input
            className="vfm-input"
            value={comparedWith}
            onChange={(e) => setComparedWith(e.target.value)}
            placeholder="Escribe el nombre si lo mencionó..."
          />
        </div>

        <div className="vfm-field">
          <label>¿En qué etapa se quedó el prospecto? <b>*</b></label>
          <Chips options={STAGES} value={stage ? [stage] : []} onToggle={setStage} />
          {err(!stage)}
        </div>

        {error && <div className="vfm-error vfm-error--box">{error}</div>}

        <div className="vfm-footer">
          <button type="button" className="vfm-btn vfm-btn--ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button type="button" className="vfm-btn vfm-btn--primary" onClick={submit} disabled={loading}>
            {loading ? 'Guardando…' : submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PreSaleVisitFeedbackModal;
